/**
 * Shared "are we actually online" state, plus the active reachability
 * probe that keeps it honest.
 *
 * ADDED 2026-08-31 (Duane): "the submit button takes a long time to
 * enable, the sync icon is turning even though I'm offline... offline
 * detection is not working correctly." Every prior fix in this area
 * (see authAtoms.ts's isOnlineAtom, useAuthBootstrap.ts's visibility/
 * focus recheck, useOfflineMutation.ts's fresh navigator.onLine
 * check) still ultimately trusted `navigator.onLine`/the window
 * 'online'/'offline' events as ground truth. Those only report
 * whether the OS thinks SOME network interface is up — not whether it
 * can actually reach anything — and real phones can be slow (seconds,
 * sometimes longer) to notice a manually-disabled wifi radio and fire
 * 'offline', or can keep reporting `navigator.onLine === true` the
 * whole time. While that's stale-true, apolloClient.ts's timeoutLink
 * picks its 15s "online" ceiling instead of the 2s "offline" one for
 * every unconditional watchQuery leg (WellsListPage's wells table,
 * DailyReportPage's report-history fetch that gates Submit) — that's
 * the actual mechanism behind "takes a long time," not a hang bug.
 *
 * This module is the fix: a plain (non-Jotai, non-React) mutable flag
 * that both React code (via authAtoms.ts's isOnlineAtom, which mirrors
 * every write here — see its own comment) and non-React code
 * (apolloClient.ts's timeoutLink, which can't import authAtoms.ts
 * without creating a circular import — see that file's own comment on
 * the same problem) can read the exact same, actively-verified value
 * from. Kept deliberately dependency-free (no app imports) so it can
 * sit underneath both.
 */

let online = typeof navigator !== 'undefined' ? navigator.onLine : true;

export function getOnlineState(): boolean {
   return online;
}

/**
 * Every writer in the app (useAuthBootstrap's event listeners/poll,
 * apolloClient's real-request-outcome link) should call this — never
 * assign to a local copy — so getOnlineState() never goes stale for
 * whichever caller happens to read it next.
 */
export function setOnlineState(value: boolean): void {
   online = value;
}

// Any static asset that's genuinely part of the built app and always
// served from the same origin (so no CORS concerns), regardless of
// environment — see index.html's own favicon links, which already
// depend on this exact file existing in both `npm run dev` and a
// production build. Cache-busted via the query param below so a
// browser or intermediate cache can't quietly turn this into a
// same-answer-forever check.
const PROBE_PATH = '/images/logo-small.png';

const DEFAULT_PROBE_TIMEOUT_MS = 3000;

/**
 * Actively verifies reachability instead of trusting an interface
 * flag: any HTTP response at all (even a 404, if the asset ever
 * moves) proves the network path genuinely goes somewhere, so that
 * counts as online. Only a real network-level failure — no route, DNS
 * failure, or this probe's own timeout — means genuinely offline.
 *
 * Deliberately does NOT short-circuit on `navigator.onLine === false`
 * first: that flag is exactly the signal this probe exists to not
 * blindly trust (see this module's own top comment) — a device with a
 * disabled wifi radio that still has, say, cellular data should be
 * found reachable, not assumed offline just because one interface
 * looks down.
 */
export async function probeReachability(timeoutMs: number = DEFAULT_PROBE_TIMEOUT_MS): Promise<boolean> {
   const controller = new AbortController();
   const timer = setTimeout(() => controller.abort(), timeoutMs);
   try {
      await fetch(`${PROBE_PATH}?probe=${Date.now()}`, {
         method: 'HEAD',
         cache: 'no-store',
         signal: controller.signal,
      });
      return true;
   } catch {
      return false;
   } finally {
      clearTimeout(timer);
   }
}
